import React, { useState, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Chip,
  Card,
  CardContent,
  InputAdornment,
  Alert,
  Snackbar,
  Fab,
  Tooltip,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Camera as CameraIcon,
  PhotoLibrary as PhotoLibraryIcon,
  QrCode as QrCodeIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  Badge as BadgeIcon,
  Print as PrintIcon
} from '@mui/icons-material';

// Department and Position mappings
const DEPARTMENTS_AND_POSITIONS = {
  "Administrative Positions": [
    "Hospital Administrator / CEO",
    "Medical Director",
    "Chief Operating Officer (COO)",
    "Chief Financial Officer (CFO)",
    "Human Resource Manager",
    "Administrative Officer",
    "Receptionist / Front Desk Officer",
    "Billing Officer",
    "Health Information Manager (Medical Records Officer)"
  ],
  "Medical Staff": [
    "Consultant / Specialist Doctor",
    "Medical Officer / General Physician",
    "Resident Doctor",
    "House Officer / Intern",
    "Surgeon",
    "Anesthesiologist",
    "Radiologist",
    "Pathologist",
    "Medical Laboratory Scientist",
    "Pharmacist",
    "Physiotherapist",
    "Psychiatrist"
  ],
  "Nursing Staff": [
    "Chief Nursing Officer (CNO)",
    "Matron / Head Nurse",
    "Nurse Manager / Supervisor",
    "Registered Nurse (RN)",
    "Enrolled Nurse (EN)",
    "Nursing Assistant",
    "Midwife"
  ],
  "Technical & Support Staff": [
    "Laboratory Technician",
    "Radiology Technician",
    "Pharmacy Technician",
    "Health Assistant / Nursing Aide",
    "Mortuary Attendant",
    "Ward Assistant",
    "Theatre Technician"
  ],
  "Facility & Support Services": [
    "Maintenance Officer",
    "ICT Officer / Health IT Specialist",
    "Security Officer",
    "Driver (e.g. Ambulance Driver)",
    "Cleaner / Janitor",
    "Catering / Kitchen Staff",
    "Laundry Staff"
  ],
  "Other Roles": [
    "Social Worker",
    "Public Health Officer",
    "Health Educator",
    "Volunteer / Intern",
    "Community Health Nurse / Officer"
  ]
};

// QR Code generator function - generates concise staff information for faster scanning
const generateQRCode = (data) => {
  // Create a more concise format for faster QR code scanning
  const staffInfo = `HOSPITAL STAFF ID
Name: ${data.name}
ID: ${data.id}
Position: ${data.position}
Department: ${data.department}
Phone: ${data.phone}
Email: ${data.email}
Status: ${new Date(data.expiryDate) > new Date() ? 'VALID' : 'EXPIRED'}
Expires: ${data.expiryDate}

Emergency Contact: ${data.phone}
Hospital Medical Center
Generated: ${new Date().toLocaleDateString()}`;
  
  // Generate QR code with optimal settings for faster scanning
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(staffInfo)}&ecc=L&color=000000&bgcolor=ffffff`;
  return qrCodeUrl;
};

// Generate unique ID based on date and time
const generateStaffId = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');
  return `STF${year}${month}${day}${hours}${minutes}${seconds}`;
};

// Calculate expiry date (1 year from registration)
const calculateExpiryDate = (registrationDate) => {
  const expiry = new Date(registrationDate);
  expiry.setFullYear(expiry.getFullYear() + 1);
  return expiry.toISOString().split('T')[0];
};

const StaffRegistation = () => {
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCamera, setShowCamera] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [selectedStaffForQR, setSelectedStaffForQR] = useState(null);
  const [idCardDialogOpen, setIdCardDialogOpen] = useState(false);
  const [selectedStaffForIdCard, setSelectedStaffForIdCard] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const [stream, setStream] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    gender: '',
    phone: '',
    email: '',
    address: '',
    department: '',
    position: '',
    image: null,
    imagePreview: null
  });

  const resetForm = () => {
    setFormData({
      name: '',
      gender: '',
      phone: '',
      email: '',
      address: '',
      department: '',
      position: '',
      image: null,
      imagePreview: null
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'department' && { position: '' }) // Reset position when department changes
    }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData(prev => ({
          ...prev,
          image: file,
          imagePreview: e.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ video: true });
      setStream(mediaStream);
      setShowCamera(true);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
      }, 100);
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Error accessing camera: ' + err.message,
        severity: 'error'
      });
    }
  };

  const captureImage = () => {
    if (videoRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      const video = videoRef.current;
      const context = canvas.getContext('2d');
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      context.drawImage(video, 0, 0);
      
      canvas.toBlob((blob) => {
        const file = new File([blob], 'captured-image.jpg', { type: 'image/jpeg' });
        const reader = new FileReader();
        reader.onload = (e) => {
          setFormData(prev => ({
            ...prev,
            image: file,
            imagePreview: e.target.result
          }));
        };
        reader.readAsDataURL(file);
      }, 'image/jpeg');
      
      stopCamera();
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setShowCamera(false);
  };

  const handleSubmit = () => {
    if (!formData.name || !formData.email || !formData.phone || !formData.department || !formData.position) {
      setSnackbar({
        open: true,
        message: 'Please fill in all required fields',
        severity: 'error'
      });
      return;
    }

    const now = new Date();
    const registrationDate = now.toISOString().split('T')[0];

    if (editingStaff) {
      // Update existing staff
      setStaff(prev => prev.map(s => 
        s.id === editingStaff.id 
          ? { ...s, ...formData, registrationDate: s.registrationDate, expiryDate: s.expiryDate }
          : s
      ));
      setSnackbar({
        open: true,
        message: 'Staff updated successfully',
        severity: 'success'
      });
    } else {
      // Add new staff
      const newStaff = {
        ...formData,
        id: generateStaffId(),
        registrationDate,
        expiryDate: calculateExpiryDate(registrationDate)
      };
      setStaff(prev => [...prev, newStaff]);
      setSnackbar({
        open: true,
        message: 'Staff added successfully',
        severity: 'success'
      });
    }

    handleClose();
  };

  const handleEdit = (staffMember) => {
    setEditingStaff(staffMember);
    setFormData({ ...staffMember });
    setOpen(true);
  };

  const handleDelete = (id) => {
    setStaff(prev => prev.filter(s => s.id !== id));
    setSnackbar({
      open: true,
      message: 'Staff deleted successfully',
      severity: 'success'
    });
  };

  const handleClose = () => {
    setOpen(false);
    setEditingStaff(null);
    resetForm();
    stopCamera();
  };

  const showQRCode = (staffMember) => {
    setSelectedStaffForQR(staffMember);
    setQrDialogOpen(true);
  };

  const showIdCard = (staffMember) => {
    setSelectedStaffForIdCard(staffMember);
    setIdCardDialogOpen(true);
  };

  const printIdCard = () => {
    if (!selectedStaffForIdCard) return;

    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    
    if (printWindow) {
      const printHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Staff ID Card - ${selectedStaffForIdCard.name}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            body {
              font-family: 'Arial', sans-serif;
              background: white;
              padding: 0;
              margin: 0;
            }
            
            .page {
              width: 100vw;
              height: 100vh;
              display: flex;
              align-items: center;
              justify-content: center;
              page-break-after: always;
            }
            
            .page:last-child {
              page-break-after: auto;
            }
            
            .id-card {
              width: 3.375in;
              height: 2.125in;
              background: white;
              border-radius: 12px;
              overflow: hidden;
              position: relative;
              box-shadow: 0 4px 12px rgba(0,0,0,0.1);
              border: 1px solid #e0e0e0;
            }
            
            /* Front Card Styles */
            .front-card {
              background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
            }
            
            .card-background {
              position: absolute;
              top: 0;
              right: 0;
              width: 100%;
              height: 100%;
              background: linear-gradient(135deg, #1976d2 0%, #2196f3 50%, #ff9800 100%);
              clip-path: polygon(60% 0%, 100% 0%, 100% 40%, 80% 100%, 40% 100%);
              opacity: 0.1;
            }
            
            .card-accent {
              position: absolute;
              top: 0;
              right: 0;
              width: 60%;
              height: 30%;
              background: linear-gradient(135deg, #1976d2 0%, #ff9800 100%);
              clip-path: polygon(40% 0%, 100% 0%, 100% 100%, 0% 100%);
            }
            
            .card-content {
              position: relative;
              z-index: 2;
              padding: 12px;
              height: 100%;
              display: flex;
              flex-direction: column;
            }
            
            .card-header {
              display: flex;
              align-items: center;
              margin-bottom: 8px;
            }
            
            .hospital-logo {
              width: 24px;
              height: 24px;
              border-radius: 4px;
              background: linear-gradient(135deg, #1976d2 0%, #2196f3 100%);
              display: flex;
              align-items: center;
              justify-content: center;
              margin-right: 8px;
              color: white;
              font-weight: bold;
              font-size: 12px;
            }
            
            .hospital-name {
              font-size: 10px;
              font-weight: bold;
              color: #1976d2;
            }
            
            .main-content {
              display: flex;
              flex: 1;
              gap: 12px;
            }
            
            .photo-section {
              display: flex;
              flex-direction: column;
              align-items: center;
              min-width: 60px;
            }
            
            .staff-photo {
              width: 60px;
              height: 60px;
              border-radius: 8px;
              object-fit: cover;
              border: 2px solid white;
              box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            }
            
            .photo-placeholder {
              width: 60px;
              height: 60px;
              border-radius: 8px;
              background: #f5f5f5;
              display: flex;
              align-items: center;
              justify-content: center;
              border: 2px solid #e0e0e0;
              color: #999;
              font-size: 24px;
            }
            
            .info-section {
              flex: 1;
              display: flex;
              flex-direction: column;
              justify-content: center;
            }
            
            .staff-name {
              font-size: 14px;
              font-weight: bold;
              color: #333;
              margin-bottom: 2px;
              line-height: 1.2;
            }
            
            .staff-position {
              font-size: 10px;
              color: #1976d2;
              font-weight: 600;
              margin-bottom: 8px;
            }
            
            .contact-info {
              display: flex;
              flex-direction: column;
              gap: 3px;
            }
            
            .contact-item {
              display: flex;
              align-items: center;
              gap: 4px;
              font-size: 8px;
              color: #666;
            }
            
            .contact-icon {
              width: 12px;
              height: 12px;
              border-radius: 50%;
              background: #ff9800;
              display: flex;
              align-items: center;
              justify-content: center;
              color: white;
              font-size: 6px;
            }
            
            .card-footer {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-top: auto;
              padding-top: 6px;
              border-top: 1px solid #e0e0e0;
            }
            
            .footer-info {
              font-size: 7px;
              color: #999;
              line-height: 1.2;
            }
            
            .qr-code {
              width: 32px;
              height: 32px;
            }
            
            /* Back card styles */
            .back-card {
              background: linear-gradient(135deg, #424242 0%, #616161 100%);
              color: white;
            }
            
            .back-card .card-background {
              background: linear-gradient(135deg, #333 0%, #555 50%, #ff9800 100%);
            }
            
            .back-title {
              text-align: center;
              font-size: 12px;
              font-weight: bold;
              margin-bottom: 12px;
              color: white;
            }
            
            .back-info {
              font-size: 9px;
              line-height: 1.4;
              margin-bottom: 8px;
              color: rgba(255,255,255,0.9);
            }
            
            .back-footer {
              position: absolute;
              bottom: 8px;
              left: 12px;
              right: 12px;
              text-align: center;
              border-top: 1px solid rgba(255,255,255,0.3);
              padding-top: 4px;
              font-size: 6px;
              opacity: 0.8;
              color: rgba(255,255,255,0.8);
            }
            
            @media print {
              body {
                margin: 0;
                padding: 0;
              }
              
              .page {
                width: 100%;
                height: 100vh;
                margin: 0;
                padding: 0;
                page-break-after: always;
              }
              
              .page:last-child {
                page-break-after: auto;
              }
              
              .id-card {
                box-shadow: none;
              }
            }
          </style>
        </head>
        <body>
          <!-- Page 1: Front of ID Card -->
          <div class="page">
            <div class="id-card front-card">
              <div class="card-background"></div>
              <div class="card-accent"></div>
              
              <div class="card-content">
                <div class="card-header">
                  <div class="hospital-logo">H</div>
                  <div class="hospital-name">Hospital Medical Center</div>
                </div>
                
                <div class="main-content">
                  <div class="photo-section">
                    ${selectedStaffForIdCard.imagePreview ? 
                      `<img src="${selectedStaffForIdCard.imagePreview}" alt="Staff Photo" class="staff-photo" />` :
                      `<div class="photo-placeholder">👤</div>`
                    }
                  </div>
                  
                  <div class="info-section">
                    <div class="staff-name">${selectedStaffForIdCard.name}</div>
                    <div class="staff-position">${selectedStaffForIdCard.position}</div>
                    
                    <div class="contact-info">
                      <div class="contact-item">
                        <div class="contact-icon">📞</div>
                        <span>${selectedStaffForIdCard.phone}</span>
                      </div>
                      <div class="contact-item">
                        <div class="contact-icon">✉</div>
                        <span>${selectedStaffForIdCard.email}</span>
                      </div>
                      <div class="contact-item">
                        <div class="contact-icon">🏢</div>
                        <span>${selectedStaffForIdCard.department}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div class="card-footer">
                  <div class="footer-info" style="text-align: center;">
                    ID: ${selectedStaffForIdCard.id} | Valid: ${selectedStaffForIdCard.expiryDate}<br />
                    🏥 Hospital Medical Center
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Page 2: Back of ID Card -->
          <div class="page">
            <div class="id-card back-card">
              <div class="card-background"></div>
              
              <div class="card-content">
                <div class="back-title">SCAN FOR STAFF DETAILS</div>
                
                <div style="display: flex; justify-content: center; align-items: center; flex: 1; margin: 8px 0;">
                  <img
                    src="${generateQRCode(selectedStaffForIdCard)}"
                    alt="QR Code - Scan for complete staff information"
                    style="width: 100px; height: 100px; background: white; padding: 4px; border-radius: 8px;"
                  />
                </div>
                
                <div style="text-align: center; margin-bottom: 12px;">
                  <div style="font-size: 8px; margin-bottom: 4px; font-weight: bold;">
                    📱 Scan with any QR reader
                  </div>
                  <div style="font-size: 7px; opacity: 0.8;">
                    Get instant access to complete staff information
                  </div>
                </div>
                
                <div class="back-footer">
                  Hospital Medical Center - Official Staff ID<br />
                  For verification: +1-555-HOSPITAL
                </div>
              </div>
            </div>
          </div>
        </body>
        </html>
      `;
      
      printWindow.document.write(printHTML);
      printWindow.document.close();
      
      // Wait for content to load then print
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      }, 1000);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredStaff = staff.filter(s =>
    s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.department.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.position.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate paginated data
  const paginatedStaff = filteredStaff.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const availablePositions = formData.department ? DEPARTMENTS_AND_POSITIONS[formData.department] || [] : [];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 'bold', color: '#1976d2' }}>
        Hospital Staff Registration System
      </Typography>

      {/* Search and Add Button */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          label="Search Staff"
          variant="outlined"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(0); // Reset to first page when searching
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1 }}
        />
        <Fab
          color="primary"
          aria-label="add"
          onClick={() => setOpen(true)}
          sx={{ ml: 2 }}
        >
          <AddIcon />
        </Fab>
      </Box>

      {/* Staff Table */}
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell>Photo</TableCell>
              <TableCell>Staff ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Gender</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Department</TableCell>
              <TableCell>Position</TableCell>
              <TableCell>Registration Date</TableCell>
              <TableCell>Expiry Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedStaff.map((staffMember) => (
              <TableRow key={staffMember.id} hover>
                <TableCell>
                  <Avatar
                    src={staffMember.imagePreview}
                    sx={{ width: 40, height: 40 }}
                  >
                    <PersonIcon />
                  </Avatar>
                </TableCell>
                <TableCell>{staffMember.id}</TableCell>
                <TableCell>{staffMember.name}</TableCell>
                <TableCell>{staffMember.gender}</TableCell>
                <TableCell>{staffMember.phone}</TableCell>
                <TableCell>{staffMember.email}</TableCell>
                <TableCell>
                  <Chip 
                    label={staffMember.department} 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>{staffMember.position}</TableCell>
                <TableCell>{staffMember.registrationDate}</TableCell>
                <TableCell>
                  <Chip 
                    label={staffMember.expiryDate} 
                    size="small" 
                    color={new Date(staffMember.expiryDate) < new Date() ? 'error' : 'success'}
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Edit">
                      <IconButton onClick={() => handleEdit(staffMember)} color="primary">
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton onClick={() => handleDelete(staffMember.id)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Generate QR Code">
                      <IconButton onClick={() => showQRCode(staffMember)} color="secondary">
                        <QrCodeIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="View ID Card">
                      <IconButton onClick={() => showIdCard(staffMember)} color="info">
                        <BadgeIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {paginatedStaff.length === 0 && (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    {filteredStaff.length === 0 ? 'No staff found' : 'No data available'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        
        {/* Table Pagination */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredStaff.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: '1px solid #e0e0e0',
            '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
              margin: 0,
            },
          }}
        />
      </TableContainer>

      {/* Add/Edit Staff Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingStaff ? 'Edit Staff' : 'Add New Staff'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Image Upload Section */}
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Avatar
                  src={formData.imagePreview}
                  sx={{ width: 120, height: 120, mx: 'auto', mb: 2 }}
                >
                  <PersonIcon sx={{ fontSize: 60 }} />
                </Avatar>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                    ref={fileInputRef}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<PhotoLibraryIcon />}
                    onClick={() => fileInputRef.current?.click()}
                    size="small"
                  >
                    Browse
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<CameraIcon />}
                    onClick={startCamera}
                    size="small"
                  >
                    Camera
                  </Button>
                </Box>
              </Box>
            </Grid>

            {/* Form Fields */}
            <Grid item xs={12} md={8}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    name="name"
                    label="Full Name"
                    value={formData.name}
                    onChange={handleInputChange}
                    fullWidth
                    required
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <FormLabel>Gender</FormLabel>
                    <RadioGroup
                      name="gender"
                      value={formData.gender}
                      onChange={handleInputChange}
                      row
                    >
                      <FormControlLabel value="Male" control={<Radio />} label="Male" />
                      <FormControlLabel value="Female" control={<Radio />} label="Female" />
                      <FormControlLabel value="Other" control={<Radio />} label="Other" />
                    </RadioGroup>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    name="phone"
                    label="Phone Number"
                    value={formData.phone}
                    onChange={handleInputChange}
                    fullWidth
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    name="email"
                    label="Email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    fullWidth
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    name="address"
                    label="Address"
                    value={formData.address}
                    onChange={handleInputChange}
                    fullWidth
                    multiline
                    rows={2}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Department</InputLabel>
                    <Select
                      name="department"
                      value={formData.department}
                      onChange={handleInputChange}
                      label="Department"
                    >
                      {Object.keys(DEPARTMENTS_AND_POSITIONS).map((dept) => (
                        <MenuItem key={dept} value={dept}>
                          {dept}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required disabled={!formData.department}>
                    <InputLabel>Position</InputLabel>
                    <Select
                      name="position"
                      value={formData.position}
                      onChange={handleInputChange}
                      label="Position"
                    >
                      {availablePositions.map((position) => (
                        <MenuItem key={position} value={position}>
                          {position}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingStaff ? 'Update' : 'Add'} Staff
          </Button>
        </DialogActions>
      </Dialog>

      {/* Camera Dialog */}
      <Dialog open={showCamera} onClose={stopCamera} maxWidth="sm" fullWidth>
        <DialogTitle>
          Capture Photo
          <IconButton
            onClick={stopCamera}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center' }}>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              style={{ width: '100%', maxWidth: '400px' }}
            />
            <canvas ref={canvasRef} style={{ display: 'none' }} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={stopCamera}>Cancel</Button>
          <Button onClick={captureImage} variant="contained">
            Capture
          </Button>
        </DialogActions>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={qrDialogOpen} onClose={() => setQrDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Staff QR Code</DialogTitle>
        <DialogContent>
          {selectedStaffForQR && (
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Avatar
                  src={selectedStaffForQR.imagePreview}
                  sx={{ width: 80, height: 80, mx: 'auto', mb: 2 }}
                >
                  <PersonIcon sx={{ fontSize: 40 }} />
                </Avatar>
                <Typography variant="h6" gutterBottom>
                  {selectedStaffForQR.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {selectedStaffForQR.position}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {selectedStaffForQR.department}
                </Typography>
                <Box sx={{ my: 2 }}>
                  <img
                    src={generateQRCode(selectedStaffForQR)}
                    alt="QR Code"
                    style={{ width: '300px', height: '300px' }}
                  />
                </Box>
                <Typography variant="caption" display="block" gutterBottom>
                  Staff ID: {selectedStaffForQR.id}
                </Typography>
                <Typography variant="caption" display="block" color="primary" sx={{ mt: 1 }}>
                  📱 Scan for quick staff verification
                </Typography>
                <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="caption" display="block" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Quick Scan Preview:
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ textAlign: 'left', fontSize: '10px', fontFamily: 'monospace', whiteSpace: 'pre-line' }}>
                    {`HOSPITAL STAFF ID
Name: ${selectedStaffForQR.name}
ID: ${selectedStaffForQR.id}
Position: ${selectedStaffForQR.position}
Department: ${selectedStaffForQR.department}
Phone: ${selectedStaffForQR.phone}
Email: ${selectedStaffForQR.email}
Status: ${new Date(selectedStaffForQR.expiryDate) > new Date() ? 'VALID' : 'EXPIRED'}
Expires: ${selectedStaffForQR.expiryDate}

Emergency Contact: ${selectedStaffForQR.phone}
Hospital Medical Center`}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ID Card Dialog */}
      <Dialog open={idCardDialogOpen} onClose={() => setIdCardDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Staff ID Card
          <IconButton
            onClick={printIdCard}
            sx={{ position: 'absolute', right: 50, top: 8 }}
            color="primary"
          >
            <PrintIcon />
          </IconButton>
          <IconButton
            onClick={() => setIdCardDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedStaffForIdCard && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
              {/* Front Card Preview */}
              <Card sx={{ 
                width: '300px', 
                height: '190px',
                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                color: '#333',
                position: 'relative',
                overflow: 'hidden',
                border: 'none',
                borderRadius: '12px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
              }}>
                {/* Background Design Elements */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(135deg, #1976d2 0%, #2196f3 50%, #ff9800 100%)',
                    clipPath: 'polygon(60% 0%, 100% 0%, 100% 100%, 0% 100%)',
                    opacity: 0.1
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: '-20px',
                    right: '-20px',
                    width: '120px',
                    height: '120px',
                    background: 'linear-gradient(135deg, #1976d2 0%, #2196f3 100%)',
                    borderRadius: '50%',
                    opacity: 0.8
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: '-30px',
                    left: '-30px',
                    width: '80px',
                    height: '80px',
                    background: 'linear-gradient(45deg, #ff9800 0%, #f57c00 100%)',
                    borderRadius: '50%',
                    opacity: 0.6
                  }}
                />
                
                <CardContent sx={{ p: 2, height: '100%', position: 'relative', zIndex: 2 }}>
                  {/* Header */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Box sx={{ 
                      width: 32, 
                      height: 32, 
                      borderRadius: 1, 
                      background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 1.5,
                      boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)'
                    }}>
                      <Typography sx={{ color: 'white', fontWeight: 'bold', fontSize: '14px' }}>
                        H
                      </Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 'bold', fontSize: '12px', color: '#1976d2' }}>
                      Hospital Medical Center
                    </Typography>
                  </Box>
                  
                  {/* Main Content */}
                  <Box sx={{ display: 'flex', gap: 2, height: 'calc(100% - 80px)' }}>
                    {/* Photo Section */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                      <Avatar
                        src={selectedStaffForIdCard.imagePreview}
                        sx={{ 
                          width: 60, 
                          height: 60, 
                          border: '2px solid white',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          borderRadius: 1.5
                        }}
                      >
                        <PersonIcon sx={{ fontSize: 30 }} />
                      </Avatar>
                    </Box>
                    
                    {/* Info Section */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <Box>
                        <Typography sx={{ fontWeight: 'bold', fontSize: '14px', mb: 0.5, color: '#333', lineHeight: 1.1 }}>
                          {selectedStaffForIdCard.name}
                        </Typography>
                        <Typography sx={{ fontSize: '10px', mb: 1, color: '#1976d2', fontWeight: '600' }}>
                          {selectedStaffForIdCard.position}
                        </Typography>
                      </Box>
                      
                      {/* Contact Info with Icons */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ 
                            width: 16, 
                            height: 16, 
                            borderRadius: '50%', 
                            background: '#ff9800',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <Typography sx={{ color: 'white', fontSize: '8px', fontWeight: 'bold' }}>
                              📞
                            </Typography>
                          </Box>
                          <Typography sx={{ fontSize: '8px', color: '#666', fontWeight: '500' }}>
                            {selectedStaffForIdCard.phone}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ 
                            width: 16, 
                            height: 16, 
                            borderRadius: '50%', 
                            background: '#ff9800',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <Typography sx={{ color: 'white', fontSize: '8px', fontWeight: 'bold' }}>
                              📧
                            </Typography>
                          </Box>
                          <Typography sx={{ fontSize: '8px', color: '#666', fontWeight: '500' }}>
                            {selectedStaffForIdCard.email}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ 
                            width: 16, 
                            height: 16, 
                            borderRadius: '50%', 
                            background: '#ff9800',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <Typography sx={{ color: 'white', fontSize: '8px', fontWeight: 'bold' }}>
                              📍
                            </Typography>
                          </Box>
                          <Typography sx={{ fontSize: '8px', color: '#666', fontWeight: '500' }}>
                            {selectedStaffForIdCard.department}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                  
                  {/* Bottom Section */}
                  <Box sx={{ 
                    position: 'absolute',
                    bottom: 8,
                    left: 12,
                    right: 12,
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    borderTop: '1px solid rgba(0,0,0,0.1)',
                    pt: 0.5
                  }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography sx={{ fontSize: '8px', color: '#999', display: 'block' }}>
                        ID: {selectedStaffForIdCard.id} | Valid: {selectedStaffForIdCard.expiryDate}
                      </Typography>
                      <Typography sx={{ fontSize: '7px', color: '#1976d2', fontWeight: 'bold' }}>
                        🏥 Hospital Medical Center
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Back Card Preview */}
              <Card sx={{ 
                width: '300px', 
                height: '190px',
                background: 'linear-gradient(135deg, #424242 0%, #616161 100%)',
                color: 'white',
                position: 'relative',
                border: 'none',
                borderRadius: '12px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
              }}>
                <CardContent sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Typography sx={{ textAlign: 'center', mb: 2, fontWeight: 'bold', fontSize: '12px' }}>
                    SCAN FOR STAFF DETAILS
                  </Typography>
                  
                  {/* Large QR Code */}
                  <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center', flex: 1, alignItems: 'center' }}>
                    <img
                      src={generateQRCode(selectedStaffForIdCard)}
                      alt="QR Code - Scan for complete staff information"
                      style={{ 
                        width: '120px', 
                        height: '120px',
                        backgroundColor: 'white',
                        padding: '4px',
                        borderRadius: '8px'
                      }}
                    />
                  </Box>
                  
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '8px', mb: 0.5, opacity: 0.9 }}>
                      📱 <strong>Scan with any QR reader</strong>
                    </Typography>
                    <Typography sx={{ fontSize: '7px', opacity: 0.7 }}>
                      Get instant access to staff information
                    </Typography>
                  </Box>
                  
                  <Box sx={{ 
                    position: 'absolute', 
                    bottom: 8, 
                    left: 12, 
                    right: 12,
                    textAlign: 'center',
                    borderTop: '1px solid rgba(255,255,255,0.3)',
                    pt: 0.5
                  }}>
                    <Typography sx={{ fontSize: '7px', opacity: 0.8 }}>
                      Hospital Medical Center - Official Staff ID
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={printIdCard} startIcon={<PrintIcon />} variant="outlined">
            Print ID Card
          </Button>
          <Button onClick={() => setIdCardDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StaffRegistation;






